import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';
import { money } from '../../utils/money';
import {
  BarList, Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput,
  fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import {
  EMPTY_TAG_FILTER, LIFECYCLE_LABEL, TagFilter, TagFilterValue, UserTagCells, describeTagFilter,
  tagFilterActive, tagFilterParams, tagText, toLifecycle, useTagOptions,
} from './tags/shared';

/**
 * Checkout funnel — everyone who reached the paywall or Razorpay, where each
 * of them stopped, and the whole story as one timeline.
 *
 * Why it exists (2026-09-24): Razorpay had 21 customers and most never paid,
 * but nobody could say where each one stopped without reading the Razorpay
 * dashboard and four of our own tables side by side. Backed by
 * admin_checkout_funnel.py, which joins a mirror of the gateway (customers,
 * subscriptions, payments, orders — synced every 30 min, or on demand from the
 * header), our billing tables, the local checkout-attempt log and the site
 * click stream into one row per PERSON (keyed by email, so two accounts or a
 * deleted one still come out as one), with a stage reached, an outcome, a
 * one-line drop-off reason and the event where the drop-off happened.
 *
 * Read-only apart from two actions: "Sync Razorpay now" (GETs from the
 * gateway into the mirror; creates nothing at Razorpay) and the Excel export.
 *
 * Stage filter: the dropdown's "reached at least" is the server's min-stage
 * filter; clicking a funnel bar asks for the people who STOPPED at that stage
 * (stage_exact=<the stage's index>).
 */

const S = 'funnel';
const EXPORT_PATH = '/api/v1/admin/funnel/export.xlsx';

// ---------------------------------------------------------------------------
// Vocabulary — mirrors admin_checkout_funnel's stage model and outcomes; the
// server's own labels win wherever the payload carries them.
// ---------------------------------------------------------------------------
const STAGES = ['signed_up', 'saw_paywall', 'clicked', 'checkout_opened', 'payment_attempted', 'converted'];
const STAGE_LABELS: Record<string, string> = {
  signed_up: 'Signed up',
  saw_paywall: 'Saw the paywall',
  clicked: 'Clicked upgrade / trial',
  checkout_opened: 'Opened checkout',
  payment_attempted: 'Attempted payment',
  converted: 'Converted',
};

/** A stage arrives as its index (0–5), a numeric string, or its key. */
const stageIdx = (v: any): number => {
  if (v === null || v === undefined || v === '') return -1;
  if (typeof v === 'number') return Number.isFinite(v) ? v : -1;
  const s = String(v).trim();
  if (/^\d+$/.test(s)) return Number(s);
  return STAGES.indexOf(s);
};

type Tone = 'good' | 'warn' | 'bad' | 'neutral';

/** Colour per outcome follows the workbook: green paid, amber trial or
 *  abandoned, red failed. */
const OUTCOMES: { key: string; label: string; tone: Tone }[] = [
  { key: 'paid', label: 'Paid', tone: 'good' },
  { key: 'trial_mandate_converted', label: 'Trial (mandate) → paid', tone: 'good' },
  { key: 'paid_then_cancelled_renewal', label: 'Paid, then cancelled renewal', tone: 'warn' },
  { key: 'trial_card_free', label: 'Card-free trial', tone: 'warn' },
  { key: 'trial_admin_granted', label: 'Trial granted by admin', tone: 'warn' },
  { key: 'trial_mandate_no_convert', label: 'Trial (mandate), never converted', tone: 'warn' },
  { key: 'admin_granted', label: 'Granted by admin', tone: 'neutral' },
  { key: 'mandate_auth_failed', label: 'Mandate authorisation failed', tone: 'bad' },
  { key: 'payment_failed', label: 'Payment failed', tone: 'bad' },
  { key: 'abandoned_at_checkout', label: 'Abandoned at checkout', tone: 'warn' },
  { key: 'clicked_no_checkout', label: 'Clicked, never reached checkout', tone: 'warn' },
  { key: 'saw_paywall_only', label: 'Saw the paywall only', tone: 'neutral' },
];
const OUTCOME_BY_KEY: Record<string, { label: string; tone: Tone }> =
  Object.fromEntries(OUTCOMES.map(o => [o.key, { label: o.label, tone: o.tone }]));
/** The KPI tiles count several outcomes; their click filters on the same set
 *  (the people endpoint takes a comma list). */
const PAID_OUTCOMES = 'paid,paid_then_cancelled_renewal,trial_mandate_converted';
const FAILED_OUTCOMES = 'payment_failed,mandate_auth_failed';
const OUTCOME_GROUPS: Record<string, string> = {
  [PAID_OUTCOMES]: 'Paid (any)',
  [FAILED_OUTCOMES]: 'Payment or autopay authorisation failed',
};
/** Outcomes where the person got access — no drop-off step unless an event says so. */
const CONVERTED = new Set(['paid', 'trial_mandate_converted', 'trial_card_free',
  'trial_admin_granted', 'admin_granted']);

const PILL: Record<Tone, string> = {
  good: 'text-green-700 bg-green-50 border-green-200',
  warn: 'text-amber-700 bg-amber-50 border-amber-200',
  bad: 'text-red-600 bg-red-50 border-red-200',
  neutral: 'text-grayscale-1200 bg-grayscale-200 border-grayscale-300',
};
const DOT: Record<Tone, string> = {
  good: 'bg-green-600', warn: 'bg-amber-500', bad: 'bg-red-600', neutral: 'bg-grayscale-600',
};
const CALLOUT: Record<Tone, string> = {
  good: 'border-green-600 bg-green-50 text-green-800',
  warn: 'border-amber-500 bg-amber-50 text-amber-800',
  bad: 'border-red-600 bg-red-50 text-red-800',
  neutral: 'border-grayscale-600 bg-grayscale-200 text-grayscale-1300',
};
const asTone = (t: any): Tone =>
  (t === 'good' || t === 'warn' || t === 'bad' || t === 'neutral') ? t : 'neutral';

const Pill: React.FC<{ tone: Tone; children: React.ReactNode; title?: string }> =
  ({ tone, children, title }) => (
  <span title={title}
    className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${PILL[tone]}`}>{children}</span>
);

const TEAM_OPTIONS = ['exclude', 'include', 'only'];
const TEAM_LABELS: Record<string, string> = {
  exclude: 'Without team accounts', include: 'Including team accounts', only: 'Team accounts only',
};

/** Table column → the server's sort key. Other columns are not sortable on
 *  the server, and sorting one page locally would misrank the rest. */
const SORT_BY_COL: Record<string, string> = {
  last_activity: 'last_activity', first_activity: 'first_activity',
  furthest_stage: 'stage', payment_attempts: 'attempts', paid_amount_minor: 'amount',
};

const COLS = ['person', 'lifecycle_tags', 'outcome', 'furthest_stage', 'dropoff_reason', 'checkouts',
  'payment_attempts', 'failed_payments', 'paid_amount_minor', 'searches',
  'first_activity', 'last_activity'];
const LABELS: Record<string, string> = {
  person: 'Person', lifecycle_tags: 'Lifecycle · tags', outcome: 'Outcome', furthest_stage: 'Furthest stage',
  dropoff_reason: 'Drop-off', checkouts: 'Checkouts', payment_attempts: 'Pay attempts',
  failed_payments: 'Failed', paid_amount_minor: 'Paid', searches: 'Searches',
  first_activity: 'First activity', last_activity: 'Last activity',
};

type Filters = {
  q: string; outcome: string; stage: string; stageMode: 'min' | 'exact';
  team: string; from: string; to: string;
};
const EMPTY: Filters = {
  q: '', outcome: '', stage: '', stageMode: 'min', team: 'exclude', from: '', to: '',
};

/** A person's lifecycle + tags as text (user_tags.decorate, resolved by the
 *  server from the row's first account id or e-mail) — what the table's own
 *  search, column filter and CSV see. */
const lifecycleText = (r: any) => {
  const lc = toLifecycle(r?.lifecycle);
  return [r?.lifecycle_label || LIFECYCLE_LABEL[lc] || lc, tagText(r)].filter(Boolean).join(' · ');
};

/** Keeps name/email searches off every keystroke. */
function useDebounced(value: string, msDelay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), msDelay);
    return () => clearTimeout(t);
  }, [value, msDelay]);
  return v;
}

const list = (v: any): string[] =>
  Array.isArray(v) ? v.filter(x => x !== null && x !== undefined && x !== '').map(String)
    : (v ? [String(v)] : []);

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const saveBlob = (blob: Blob, name: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

/** "3 h later" between two timeline events — a long silence before the last
 *  event is often the whole story of a drop-off. */
const gapLabel = (prev?: string, cur?: string): string => {
  if (!prev || !cur) return '';
  const s = (new Date(cur).getTime() - new Date(prev).getTime()) / 1000;
  if (!Number.isFinite(s) || s < 3600) return '';
  if (s < 86400) return `${Math.round(s / 3600)} h later`;
  return `${Math.round(s / 86400)} d later`;
};

// Timeline filters. Site clicks are "clicks"; everything from the gateway or
// our billing tables is "billing"; the sheet's own events (opened, closed,
// payment failed) are clicks that are also billing, so they show in both.
const evBlob = (e: any) => `${e?.source || ''} ${e?.kind || ''}`.toLowerCase();
const isClickEv = (e: any) => /site|click/.test(evBlob(e));
const isBillingEv = (e: any) => {
  if (/signup|sign_up|search/.test(evBlob(e))) return false;
  if (!isClickEv(e)) return true;
  return /checkout|payment|razorpay/i.test(`${e?.kind || ''} ${e?.title || ''}`);
};

// ---------------------------------------------------------------------------
// Person drawer — identity, outcome callout, stage steps, the timeline
// ---------------------------------------------------------------------------
const StageSteps: React.FC<{ labels: string[]; furthest: number; drop: number }> =
  ({ labels, furthest, drop }) => (
  <ol className="grid grid-cols-6">
    {labels.map((l, i) => {
      const reached = i <= furthest;
      const isDrop = i === drop;
      const circle = isDrop
        ? 'bg-red-600 border-red-600 text-white'
        : reached ? 'bg-primary-500 border-primary-500 text-white'
          : 'bg-white border-grayscale-600 text-grayscale-1100';
      return (
        <li key={i} className="relative flex flex-col items-center text-center gap-1 px-0.5"
          title={isDrop ? `Dropped off at: ${l}` : reached ? `Reached: ${l}` : `Not reached: ${l}`}>
          {i > 0 && (
            <span aria-hidden
              className={`absolute top-[11px] right-1/2 w-full h-0.5 ${i <= furthest ? 'bg-primary-500' : 'bg-grayscale-200'}`} />
          )}
          <span className={`relative z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center
            text-[11px] font-bold ${circle}`}>
            {isDrop ? '!' : reached ? '✓' : i}
          </span>
          <span className={`text-[10px] leading-tight ${isDrop ? 'text-red-700 font-bold'
            : reached ? 'text-grayscale-1300' : 'text-grayscale-1100'}`}>{l}</span>
          {isDrop && <span className="text-[9px] uppercase tracking-wide font-bold text-red-700">drop-off</span>}
        </li>
      );
    })}
  </ol>
);

const PersonDrawer: React.FC<{
  personKey: string; onClose: () => void;
  stageLabels: string[]; outcomeLabel: (k: string) => string;
}> = ({ personKey, onClose, stageLabels, outcomeLabel }) => {
  const [person, setPerson] = useState<any>(null);
  const [error, setError] = useState('');
  const [view, setView] = useState<'all' | 'billing' | 'clicks'>('all');
  const [copied, setCopied] = useState('');
  const dropRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Centre the drop-off inside the drawer's own scroller. scrollIntoView would
  // also scroll the page behind the drawer, losing the admin's place in the list.
  const scrollToDrop = () => {
    const el = dropRef.current, box = bodyRef.current;
    if (!el || !box) return;
    const r = el.getBoundingClientRect(), b = box.getBoundingClientRect();
    box.scrollTo({ top: box.scrollTop + (r.top - b.top) - Math.max(0, (b.height - r.height) / 2),
                   behavior: 'smooth' });
  };

  useEffect(() => {
    let alive = true;
    setPerson(null); setError(''); setView('all');
    panelGet(S, 'person', { key: personKey }).then(({ data, error }) => {
      if (!alive) return;
      // {…row fields, events} — or {person: {…}, events} from a server that nests it.
      const p = data && data.person && typeof data.person === 'object'
        ? { ...data.person, events: data.events ?? data.person.events ?? [] }
        : data;
      setPerson(p); setError(error);
    });
    return () => { alive = false; };
  }, [personKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const events: any[] = useMemo(() => (Array.isArray(person?.events) ? person.events : []), [person]);
  const dropEv = useMemo(() => events.find(e => e?.dropoff), [events]);

  // Open on the drop-off: that event is why the admin clicked this row.
  useEffect(() => {
    if (!person) return;
    const t = setTimeout(scrollToDrop, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person]);

  const copy = async (text: string, tag = text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      window.prompt('Copy this:', text);
    }
  };

  const shown = events.filter(e => view === 'all' || (view === 'billing' ? isBillingEv(e) : isClickEv(e)));
  const counts = {
    all: events.length,
    billing: events.filter(isBillingEv).length,
    clicks: events.filter(isClickEv).length,
  };

  const outcome = String(person?.outcome || '');
  const tone: Tone = OUTCOME_BY_KEY[outcome]?.tone || 'neutral';
  const furthest = stageIdx(person?.furthest_stage);
  const evStage = dropEv ? stageIdx(dropEv.stage) : -1;
  const dropStage = evStage >= 0 ? evStage : (dropEv || !CONVERTED.has(outcome) ? furthest : -1);
  const emails = list(person?.emails).length ? list(person?.emails) : list(person?.key);
  const title = person?.name || emails[0] || personKey;

  const timelineText = () => shown.map(e =>
    `${fmtDate(e.at)}  ${e.dropoff ? '[DROP-OFF] ' : ''}${e.title || e.kind || ''}`
    + `${e.detail ? ` — ${e.detail}` : ''}${e.source ? ` (${e.source})` : ''}`).join('\n');

  const idRow = (label: string, values: string[]) => (
    <div className="flex gap-3 items-start text-sm py-1 border-b border-grayscale-200 last:border-0">
      <span className="text-grayscale-1100 w-24 sm:w-36 shrink-0">{label}</span>
      <div className="flex gap-1.5 flex-wrap min-w-0">
        {values.length === 0 && <span className="text-grayscale-1100">—</span>}
        {values.map(v => (
          <span key={v} className="inline-flex items-center gap-1 rounded-md border border-grayscale-300
            bg-grayscale-200 pl-2 pr-1 py-0.5 text-xs font-mono text-grayscale-1300 break-all">
            {v}
            <button type="button" onClick={() => copy(v)} title="Copy"
              className="rounded px-1 text-[10px] font-sans text-grayscale-1100 hover:bg-white">
              {copied === v ? 'Copied' : 'Copy'}
            </button>
          </span>
        ))}
      </div>
    </div>
  );

  const fact = (label: string, value: React.ReactNode) => (
    <div key={label} className="rounded-lg border border-grayscale-300 px-3 py-2 min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-grayscale-1100">{label}</div>
      <div className="text-sm text-grayscale-1300 font-medium truncate">
        {value === null || value === undefined || value === '' ? '—' : value}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-white shadow-xl flex flex-col"
        style={{ width: 'min(96vw, 64rem)' }}
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold text-grayscale-1300 font-grotesk truncate">{title}</span>
              {person?.is_team && <Pill tone="neutral" title="Team account — excluded from the funnel by default">team</Pill>}
              {outcome && <Pill tone={tone}>{outcomeLabel(outcome)}</Pill>}
            </div>
            {person?.name && emails[0] && <div className="text-xs text-grayscale-1100 truncate">{emails[0]}</div>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {events.length > 0 && (
              <Btn small onClick={() => copy(timelineText(), '__timeline__')}
                title="The timeline as it is filtered below, as plain text">
                {copied === '__timeline__' ? 'Copied' : 'Copy timeline'}
              </Btn>
            )}
            <Btn small onClick={onClose}>Close</Btn>
          </div>
        </div>

        <div ref={bodyRef} className="p-5 overflow-y-auto flex flex-col gap-4">
          {error && <Msg kind="error">{error}</Msg>}
          {!person && !error && <Loading label="Building the journey…" />}
          {person && (
            <>
              <div className={`rounded-lg border-l-4 px-4 py-3 ${CALLOUT[tone]}`}>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="uppercase tracking-wide font-bold">Outcome</span>
                  <span>{outcome ? outcomeLabel(outcome) : '—'}</span>
                  {person.trial_state && <span className="opacity-80">· trial: {String(person.trial_state)}</span>}
                </div>
                <div className="text-sm font-medium mt-1">
                  {person.dropoff_reason || (CONVERTED.has(outcome) ? 'Converted — no drop-off.' : 'No drop-off reason recorded.')}
                </div>
                {dropEv && (
                  <button type="button"
                    onClick={scrollToDrop}
                    className="text-xs underline underline-offset-2 mt-1 text-left">
                    Drop-off: {fmtDate(dropEv.at)} — {dropEv.title || dropEv.kind} ↓
                  </button>
                )}
              </div>

              <StageSteps labels={stageLabels} furthest={furthest} drop={dropStage} />

              <div>
                {idRow('Emails', emails)}
                {idRow('User ids', list(person.user_ids))}
                {idRow('Razorpay customers', list(person.customer_ids))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {fact('Signed up', person.signed_up_at ? fmtDate(person.signed_up_at) : '')}
                {fact('First activity', person.first_activity ? fmtDate(person.first_activity) : '')}
                {fact('Last activity', person.last_activity ? fmtDate(person.last_activity) : '')}
                {fact('Searches', num(person.searches).toLocaleString())}
                {fact('Checkouts', num(person.checkouts))}
                {fact('Payment attempts', num(person.payment_attempts))}
                {fact('Failed payments', num(person.failed_payments))}
                {fact('Paid', num(person.paid_amount_minor) > 0
                  ? money(num(person.paid_amount_minor), person.currency || 'INR') : '')}
              </div>

              <Section title="Timeline"
                right={
                  <div className="flex gap-1">
                    {(['all', 'billing', 'clicks'] as const).map(v => (
                      <button key={v} type="button" onClick={() => setView(v)}
                        className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap ${view === v
                          ? 'bg-primary-500 text-grayscale-100 font-bold'
                          : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                        {v === 'all' ? 'All' : v === 'billing' ? 'Only billing' : 'Only clicks'} ({counts[v]})
                      </button>
                    ))}
                  </div>
                }>
                {shown.length === 0 ? (
                  <div className="text-sm text-grayscale-1100 py-4 text-center">
                    {events.length ? 'No events of this kind.' : 'No recorded events.'}
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {shown.map((e, i) => {
                      const isDrop = !!e.dropoff;
                      const t = asTone(e.tone);
                      const gap = gapLabel(shown[i - 1]?.at, e.at);
                      const st = stageIdx(e.stage);
                      return (
                        <div key={i} ref={isDrop ? dropRef : undefined}
                          className={`flex gap-3 px-2 rounded-lg ${isDrop ? 'border-2 border-red-600 bg-red-50 my-1' : ''}`}>
                          <div className="w-20 sm:w-32 shrink-0 py-2 text-[11px] text-grayscale-1100 sm:whitespace-nowrap"
                            title={e.at || undefined}>
                            {fmtDate(e.at)}
                            {gap && <div className="text-[10px] opacity-80">{gap}</div>}
                          </div>
                          <div className="relative w-4 shrink-0" aria-hidden>
                            {shown.length > 1 && (
                              <span className="absolute left-1/2 -translate-x-1/2 w-px bg-grayscale-600"
                                style={{ top: i === 0 ? '0.95rem' : 0,
                                         bottom: i === shown.length - 1 ? 'calc(100% - 0.95rem)' : 0,
                                         opacity: 0.4 }} />
                            )}
                            <span className={`absolute left-1/2 -translate-x-1/2 top-[0.6rem] w-3 h-3 rounded-full
                              ring-2 ${isDrop ? 'ring-red-50' : 'ring-white'} ${DOT[t]}`} />
                          </div>
                          <div className="flex-1 min-w-0 py-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm text-grayscale-1300 ${isDrop ? 'font-bold' : 'font-medium'}`}>
                                {e.title || e.kind || 'Event'}
                              </span>
                              {isDrop && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-red-600 rounded-full px-2 py-0.5">
                                  Drop-off
                                </span>
                              )}
                            </div>
                            {e.detail && (
                              <div className="text-xs text-grayscale-1100 break-words whitespace-pre-wrap">{e.detail}</div>
                            )}
                            <div className="flex gap-1.5 flex-wrap mt-1">
                              {e.source && (
                                <span className="text-[10px] rounded border border-grayscale-300 bg-grayscale-200 px-1.5 py-px font-mono text-grayscale-1100">
                                  {e.source}
                                </span>
                              )}
                              {st >= 0 && stageLabels[st] && (
                                <span className="text-[10px] rounded border border-grayscale-300 px-1.5 py-px text-grayscale-1100">
                                  {stageLabels[st]}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------
const FunnelPanel: React.FC = () => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(
    { key: 'last_activity', dir: 'desc' });
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncSecs, setSyncSecs] = useState(0);
  const [syncMsg, setSyncMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null);
  const [syncRun, setSyncRun] = useState<any>(null);
  const [dlBusy, setDlBusy] = useState(false);
  const [dlErr, setDlErr] = useState('');
  const q = useDebounced(f.q);
  // Lifecycle + custom tags (the standard params, filtered server-side so
  // paging and totals stay right). People with no account match only an
  // empty tag filter.
  const [tagF, setTagF] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const tagOpts = useTagOptions();

  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));

  const filterParams = useMemo(() => {
    const p: Record<string, string> = {
      q, outcome: f.outcome, team: f.team, from: f.from, to: f.to, ...tagFilterParams(tagF),
    };
    if (f.stage !== '') {
      p.stage = f.stage;
      // The server reads stage_exact's VALUE as the stage index.
      if (f.stageMode === 'exact') p.stage_exact = f.stage;
    }
    return p;
  }, [q, f.outcome, f.team, f.from, f.to, f.stage, f.stageMode, tagF]);
  const filterKey = JSON.stringify(filterParams);

  // A new filter or sort starts again from the first page.
  useEffect(() => { setPage(0); }, [filterKey, perPage, sort]);

  // The reads a Refresh or a sync triggers (a new tick) skip the server's
  // 60-s population cache: it is per worker, and a sync clears only the
  // cache of the worker that ran it.
  const tickSent = useRef(0);
  const fresh = tick !== tickSent.current ? '1' : '';
  useEffect(() => { tickSent.current = tick; }, [tick]);

  const summary = useAdminGet(S, 'summary', { team: f.team, refresh: fresh }, [f.team, tick]);
  const people = useAdminGet(S, 'people', {
    ...filterParams, page, per_page: perPage,
    sort: sort ? (SORT_BY_COL[sort.key] || '') : '', dir: sort?.dir || '', refresh: fresh,
  }, [filterKey, page, perPage, sort, tick]);

  // The sync takes 10–20 s; a counting label says it is still working.
  useEffect(() => {
    if (!syncBusy) return;
    setSyncSecs(0);
    const t = setInterval(() => setSyncSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [syncBusy]);

  // ---- summary -----------------------------------------------------------
  const sd = summary.data || {};
  const stageRows = useMemo(() => {
    const raw: any[] = Array.isArray(sd.stages) ? sd.stages : [];
    return raw.map(s => {
      const idx = stageIdx(s.stage);
      return {
        idx, key: STAGES[idx] || String(s.stage),
        label: s.label || STAGE_LABELS[STAGES[idx]] || String(s.stage),
        people: num(s.people),
      };
    }).filter(s => s.idx >= 0).sort((a, b) => a.idx - b.idx);
  }, [sd.stages]);
  const stageLabels = STAGES.map((k, i) => stageRows.find(s => s.idx === i)?.label || STAGE_LABELS[k]);
  const stagePeople = (i: number) => stageRows.find(s => s.idx === i)?.people ?? 0;
  const funnelMax = Math.max(1, ...stageRows.map(s => s.people));

  const outcomes: { outcome: string; label: string; people: number }[] = useMemo(() =>
    (Array.isArray(sd.outcomes) ? sd.outcomes : []).map((o: any) => ({
      outcome: String(o.outcome),
      label: o.label || OUTCOME_BY_KEY[o.outcome]?.label || String(o.outcome),
      people: num(o.people),
    })), [sd.outcomes]);
  const outcomeLabel = (k: string) => OUTCOME_GROUPS[k]
    || outcomes.find(o => o.outcome === k)?.label || OUTCOME_BY_KEY[k]?.label || k;
  const outcomeCount = (...ks: string[]) =>
    outcomes.filter(o => ks.includes(o.outcome)).reduce((a, o) => a + o.people, 0);
  const population = outcomes.reduce((a, o) => a + o.people, 0) || stagePeople(0);
  const topErrors = (Array.isArray(sd.top_errors) ? sd.top_errors : [])
    .map((e: any) => ({ label: String(e.error || '(no description)'), value: num(e.count) }));
  const byWeek = (Array.isArray(sd.by_week) ? sd.by_week : []).map((w: any) => ({
    week: w.week, reached_checkout: num(w.reached_checkout), paid: num(w.paid),
    rate: num(w.reached_checkout) ? `${Math.round((num(w.paid) / num(w.reached_checkout)) * 100)}%` : '—',
  }));

  // The manual run's answer shows at once; after that the summary's own
  // record wins as soon as it is newer (the 30-minute auto-sync, say).
  const syncTs = (x: any) => { const t = Date.parse(x?.at || ''); return Number.isFinite(t) ? t : -1; };
  const sync = (syncRun && syncTs(syncRun) > syncTs(sd.sync)) ? syncRun : (sd.sync || syncRun || null);

  // ---- people ------------------------------------------------------------
  const pd = people.data || {};
  const rawRows: any[] = Array.isArray(pd.rows) ? pd.rows
    : Array.isArray(pd.people) ? pd.people : Array.isArray(people.data) ? people.data : [];
  const rows = useMemo(() => rawRows.map(r => {
    const emails = list(r.emails);
    return {
      ...r,
      person: [r.name, r.key || emails[0]].filter(Boolean).join(' · '),
      lifecycle_tags: lifecycleText(r),
    };
  }), [rawRows]);
  const total: number = num(pd.total ?? rawRows.length);
  const per = Number(perPage) || 50;
  const pages: number = num(pd.pages) || Math.max(1, Math.ceil(total / per));

  // ---- actions -----------------------------------------------------------
  const syncNow = async () => {
    setSyncBusy(true); setSyncMsg(null);
    const { data, error } = await panelPost(S, 'sync_now', {});
    setSyncBusy(false);
    if (error) { setSyncMsg({ kind: 'error', text: error }); return; }
    const run = data?.sync || data;
    // sync_now never fails the request: a run that blew up carries `error`,
    // and one that did not run (another worker, or the 30-minute clock, was
    // already syncing) carries `skipped` and fetched nothing.
    if (run?.skipped) {
      setSyncMsg({ kind: 'warn', text: `Sync skipped: ${run.skipped}. Nothing was fetched — try again in a minute.` });
      return;
    }
    if (run && typeof run === 'object') setSyncRun(run);
    const errs = list(run?.errors);
    setSyncMsg(run?.error
      ? { kind: 'error', text: `Sync failed: ${run.error}` }
      : errs.length
        ? { kind: 'error', text: `Synced with ${errs.length} error(s): ${errs.slice(0, 3).join(' · ')}` }
        : { kind: 'ok', text: 'Razorpay synced — the funnel below is rebuilt from the fresh mirror.' });
    setTick(t => t + 1);
  };

  const download = async () => {
    setDlBusy(true); setDlErr('');
    const { blob, filename, error } = await apiService.adminFetchFile(
      apiService.getStoredUserId() || '', EXPORT_PATH, { team: f.team });
    setDlBusy(false);
    if (error || !blob) { setDlErr(error || 'Download failed'); return; }
    const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    saveBlob(blob, filename || `checkout-funnel-${ymd}.xlsx`);
  };

  const toggleOutcome = (k: string) => setF(s => ({ ...s, outcome: s.outcome === k ? '' : k }));
  const toggleStage = (idx: number, mode: 'min' | 'exact') => setF(s =>
    (s.stage === String(idx) && s.stageMode === mode)
      ? { ...s, stage: '', stageMode: 'min' }
      : { ...s, stage: String(idx), stageMode: mode });

  const activeChips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) activeChips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.outcome) activeChips.push({ label: 'Outcome', value: outcomeLabel(f.outcome), clear: () => set('outcome')('') });
  if (f.stage !== '') {
    activeChips.push({
      label: f.stageMode === 'exact' ? 'Stopped at' : 'Reached at least',
      value: stageLabels[Number(f.stage)] || f.stage,
      clear: () => setF(s => ({ ...s, stage: '', stageMode: 'min' })),
    });
  }
  if (f.team !== 'exclude') activeChips.push({ label: 'Team', value: TEAM_LABELS[f.team] || f.team, clear: () => set('team')('exclude') });
  if (f.from) activeChips.push({ label: 'From', value: f.from, clear: () => set('from')('') });
  if (f.to) activeChips.push({ label: 'To', value: f.to, clear: () => set('to')('') });
  if (tagFilterActive(tagF)) {
    activeChips.push({
      label: 'Lifecycle / tags',
      value: describeTagFilter(tagF, k => tagOpts.tagByKey[k]?.label || k).join(' · '),
      clear: () => setTagF(EMPTY_TAG_FILTER),
    });
  }

  const counts = sync?.counts && typeof sync.counts === 'object' ? sync.counts : null;
  // A run that failed as a whole has `error` and no per-kind `errors`.
  const syncErrors = [...(sync?.error ? [`run failed: ${sync.error}`] : []), ...list(sync?.errors)];

  return (
    <div className="flex flex-col gap-4">
      {/* Header: the mirror's freshness, the two actions */}
      <div className="bg-white rounded-xl border border-grayscale-300 p-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="text-xs text-grayscale-1100 flex flex-col gap-0.5 min-w-0">
          {sync?.at ? (
            <>
              <span>
                <span className="font-bold text-grayscale-1300">
                  {sync.error ? 'Razorpay sync failed' : 'Razorpay last synced'} {fmtDate(sync.at)}
                </span>
                {sync.by ? ` by ${sync.by}` : ''}
                {sync.took_s != null ? ` · took ${Math.round(num(sync.took_s))} s` : ''}
                {` · ${syncErrors.length ? `${syncErrors.length} error(s)` : 'no errors'}`}
              </span>
              {counts && (
                <span>
                  {Object.entries(counts).map(([k, v]) => `${num(v).toLocaleString()} ${k}`).join(' · ')}
                </span>
              )}
              {syncErrors.length > 0 && (
                <span className="text-amber-700" title={syncErrors.join('\n')}>
                  {syncErrors.slice(0, 2).join(' · ')}{syncErrors.length > 2 ? ` · +${syncErrors.length - 2} more` : ''}
                </span>
              )}
            </>
          ) : (
            <span>
              {summary.loading && !summary.data ? 'Reading sync status…'
                : 'Razorpay has not been synced yet — gateway stages come from our own tables until it is.'}
            </span>
          )}
          <span>Auto-sync runs every 30 minutes. A sync only reads from Razorpay; it never creates or cancels anything there.</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Btn small onClick={() => setTick(t => t + 1)} disabled={syncBusy}>Refresh</Btn>
          <Btn small onClick={syncNow} disabled={syncBusy}
            title="Fetch every customer, subscription, payment and order from Razorpay (10–20 s)">
            {syncBusy ? `Syncing… ${syncSecs}s` : 'Sync Razorpay now'}
          </Btn>
          <Btn small kind="primary" onClick={download} disabled={dlBusy}
            title={`Summary, People, Timeline and Gateway payments sheets — ${(TEAM_LABELS[f.team] || f.team).toLowerCase()}`}>
            {dlBusy ? 'Preparing…' : 'Download Excel'}
          </Btn>
        </div>
      </div>
      {syncMsg && (syncMsg.kind === 'warn'
        ? <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">{syncMsg.text}</div>
        : <Msg kind={syncMsg.kind}>{syncMsg.text}</Msg>)}
      {dlErr && <Msg kind="error">Excel export failed: {dlErr}</Msg>}
      {summary.error && <Msg kind="error">{summary.error}</Msg>}

      <KPIRow>
        <KPI label="In the funnel" value={population.toLocaleString()}
          hint={sd.team_excluded === false ? 'team accounts included' : 'team accounts excluded'} />
        <KPI label="Reached checkout" value={stagePeople(3).toLocaleString()}
          onClick={() => toggleStage(3, 'min')} active={f.stage === '3' && f.stageMode === 'min'}
          hint="Razorpay sheet or order created" />
        <KPI label="Attempted payment" value={stagePeople(4).toLocaleString()}
          onClick={() => toggleStage(4, 'min')} active={f.stage === '4' && f.stageMode === 'min'}
          hint="incl. ₹5 mandate auth" />
        <KPI label="Paid" value={outcomeCount(...PAID_OUTCOMES.split(',')).toLocaleString()}
          onClick={() => toggleOutcome(PAID_OUTCOMES)} active={f.outcome === PAID_OUTCOMES}
          hint="incl. renewal stopped and card-trial conversions" />
        <KPI label="Payment failed" value={outcomeCount(...FAILED_OUTCOMES.split(',')).toLocaleString()}
          onClick={() => toggleOutcome(FAILED_OUTCOMES)} active={f.outcome === FAILED_OUTCOMES}
          hint="incl. failed autopay authorisation" />
        <KPI label="Abandoned at checkout" value={outcomeCount('abandoned_at_checkout').toLocaleString()}
          onClick={() => toggleOutcome('abandoned_at_checkout')} active={f.outcome === 'abandoned_at_checkout'}
          hint="opened Razorpay, never paid" />
      </KPIRow>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Funnel">
          {summary.loading && !summary.data ? <Loading /> : stageRows.length === 0 ? (
            <div className="text-sm text-grayscale-1100 py-4 text-center">No funnel data yet.</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {stageRows.map((s, i) => {
                const prev = i > 0 ? stageRows[i - 1].people : 0;
                const pct = i > 0 && prev ? Math.round((s.people / prev) * 100) : null;
                const on = f.stage === String(s.idx) && f.stageMode === 'exact';
                return (
                  <button key={s.key} type="button" onClick={() => toggleStage(s.idx, 'exact')}
                    title={on ? 'Filtering by this — click to clear' : `Show the people who stopped at "${s.label}"`}
                    className={`flex items-center gap-2 text-sm rounded-md px-1 -mx-1 text-left cursor-pointer
                      hover:bg-primary-50 ${on ? 'bg-primary-50 ring-1 ring-primary-300' : ''}`}>
                    <div className="w-44 truncate text-grayscale-1200">
                      <span className="text-grayscale-1100 text-xs mr-1">{s.idx}.</span>{s.label}
                    </div>
                    <div className="flex-1 bg-grayscale-200 rounded-full h-3 overflow-hidden">
                      <div className={`h-3 rounded-full ${s.idx === 5 ? 'bg-green-600' : 'bg-primary-500'}`}
                        style={{ width: `${(s.people / funnelMax) * 100}%` }} />
                    </div>
                    <div className="w-28 text-right text-grayscale-1300 font-medium whitespace-nowrap">
                      {s.people.toLocaleString()}
                      <span className="text-grayscale-1100 text-xs">
                        {pct === null ? '' : ` (${pct}% of prev)`}
                      </span>
                    </div>
                  </button>
                );
              })}
              <p className="text-xs text-grayscale-1100 mt-1">
                People who reached each stage. Click one to list those who stopped there. Site clicks
                are tracked from 2026-09-08 and the Razorpay sheet's own events from 2026-09-24 — before
                that, the early stages are inferred from Razorpay and our billing tables.
              </p>
            </div>
          )}
        </Section>
        <Section title="Outcomes">
          {summary.loading && !summary.data ? <Loading /> : outcomes.length === 0 ? (
            <div className="text-sm text-grayscale-1100 py-4 text-center">No outcomes yet.</div>
          ) : (
            <BarList items={outcomes.map(o => ({ label: o.label, value: o.people }))}
              onSelect={l => { const o = outcomes.find(x => x.label === l); if (o) toggleOutcome(o.outcome); }}
              activeLabel={f.outcome ? outcomeLabel(f.outcome) : undefined} />
          )}
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Top failure reasons">
          {summary.loading && !summary.data ? <Loading /> : topErrors.length === 0 ? (
            <div className="text-sm text-grayscale-1100 py-4 text-center">No failed payments.</div>
          ) : <BarList items={topErrors} />}
        </Section>
        <Section title="By week">
          {summary.loading && !summary.data ? <Loading /> : (
            <DataTable rows={byWeek} columns={['week', 'reached_checkout', 'paid', 'rate']}
              labels={{ week: 'Week', reached_checkout: 'Reached checkout', paid: 'Paid', rate: 'Paid / checkout' }}
              maxHeight="16rem" empty="No weekly data yet." />
          )}
        </Section>
      </div>

      <Section title="People"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} {total === 1 ? 'person' : 'people'}</span>
            {activeChips.length > 0 && (
              <Btn small onClick={() => { setF(EMPTY); setTagF(EMPTY_TAG_FILTER); }}>
                Clear {activeChips.length} filter{activeChips.length === 1 ? '' : 's'}
              </Btn>
            )}
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Search name or email…" />
          <Select value={f.outcome} onChange={set('outcome')}
            options={['', ...Object.keys(OUTCOME_GROUPS), ...OUTCOMES.map(o => o.key),
              ...outcomes.map(o => o.outcome).filter(k => !OUTCOME_BY_KEY[k])]}
            labels={{ '': 'Any outcome', ...OUTCOME_GROUPS,
              ...Object.fromEntries(OUTCOMES.map(o => [o.key, outcomeLabel(o.key)])),
              ...Object.fromEntries(outcomes.map(o => [o.outcome, o.label])) }} />
          <Select value={f.stage} onChange={v => setF(s => ({ ...s, stage: v }))}
            options={['', ...STAGES.map((_, i) => String(i))]}
            labels={{ '': 'Any stage', ...Object.fromEntries(STAGES.map((_, i) => [String(i), `${i}. ${stageLabels[i]}`])) }} />
          {f.stage !== '' && (
            <Select value={f.stageMode} onChange={v => setF(s => ({ ...s, stageMode: v === 'exact' ? 'exact' : 'min' }))}
              options={['min', 'exact']} labels={{ min: 'reached at least', exact: 'stopped exactly here' }} />
          )}
          <Select value={f.team} onChange={set('team')} options={TEAM_OPTIONS} labels={TEAM_LABELS} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">first activity from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
        </div>
        <TagFilter value={tagF} onChange={v => setTagF(v)} />
        {activeChips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {activeChips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {people.error && <Msg kind="error">{people.error}</Msg>}
        {people.loading && !people.data ? <Loading /> : (
          <DataTable rows={rows} columns={COLS} labels={LABELS}
            serverSort={sort}
            onSortChange={(key, dir) => {
              if (!key || !dir) { setSort(null); return; }
              if (SORT_BY_COL[key]) setSort({ key, dir });
            }}
            maxHeight="40rem"
            empty={activeChips.length ? 'Nobody matches these filters.' : 'Nobody has reached the paywall or checkout yet.'}
            render={{
              person: r => (
                <button type="button" onClick={() => setOpenKey(String(r.key || list(r.emails)[0] || ''))}
                  className="text-left flex flex-col" title="Open the timeline">
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium text-grayscale-1300 underline underline-offset-2 truncate max-w-[16rem]">
                      {r.name || r.key || list(r.emails)[0] || '—'}
                    </span>
                    {r.is_team && <Pill tone="neutral" title="Team account">team</Pill>}
                  </span>
                  {r.name && (r.key || list(r.emails)[0]) && (
                    <span className="text-[11px] text-grayscale-1100 truncate max-w-[18rem]">
                      {r.key || list(r.emails)[0]}
                      {list(r.emails).length > 1 ? ` +${list(r.emails).length - 1}` : ''}
                    </span>
                  )}
                </button>
              ),
              lifecycle_tags: r => <span className="inline-block min-w-[12rem]"><UserTagCells row={r} /></span>,
              outcome: r => r.outcome
                ? <Pill tone={OUTCOME_BY_KEY[r.outcome]?.tone || 'neutral'}>{outcomeLabel(r.outcome)}</Pill>
                : '—',
              furthest_stage: r => {
                const i = stageIdx(r.furthest_stage);
                return i >= 0 ? <span className="text-xs">{i}. {stageLabels[i]}</span> : '—';
              },
              dropoff_reason: r => (
                <span className="text-xs text-grayscale-1200" title={r.dropoff_reason || undefined}>
                  {r.dropoff_reason || '—'}
                </span>
              ),
              failed_payments: r => num(r.failed_payments)
                ? <span className="text-red-600 font-medium">{num(r.failed_payments)}</span> : '0',
              paid_amount_minor: r => num(r.paid_amount_minor) > 0
                ? money(num(r.paid_amount_minor), r.currency || 'INR') : '—',
              searches: r => num(r.searches).toLocaleString(),
              first_activity: r => <span title={r.first_activity}>{fmtDate(r.first_activity)}</span>,
              last_activity: r => <span title={r.last_activity}>{fmtDate(r.last_activity)}</span>,
            }}
            actions={r => (
              <Btn small onClick={() => setOpenKey(String(r.key || list(r.emails)[0] || ''))}>Timeline</Btn>
            )} />
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
            <span>
              Page {page + 1} of {pages} · showing {rawRows.length ? page * per + 1 : 0}–
              {page * per + rawRows.length} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <Select value={perPage} onChange={setPerPage} options={['25', '50', '100', '200']}
                labels={{ '25': '25 / page', '50': '50 / page', '100': '100 / page', '200': '200 / page' }} />
              <Btn small onClick={() => setPage(0)} disabled={page === 0}>« First</Btn>
              <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</Btn>
              <Btn small onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>Next ›</Btn>
              <Btn small onClick={() => setPage(pages - 1)} disabled={page >= pages - 1}>Last »</Btn>
            </div>
          </div>
        )}
      </Section>

      {openKey && (
        <PersonDrawer personKey={openKey} onClose={() => setOpenKey(null)}
          stageLabels={stageLabels} outcomeLabel={outcomeLabel} />
      )}
    </div>
  );
};

export default FunnelPanel;
